<script setup lang="ts">
import {computed, ref} from "vue";
import {EllipsisVertical, Trash2} from "@lucide/vue";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/core/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {Button} from "@/core/components/ui/button";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {useToastStore} from "@/stores/toast.store";

/**
 * The actions of the medication review, behind the ⋮ of the course header.
 *
 * One for now, and the heaviest of them: dropping the review whole. It sits
 * here rather than in the header's own markup because it carries a
 * confirmation, a call and a refusal path, where the header itself is a
 * banner.
 *
 * Leaving the course is not this component's move: it says the review is
 * gone, and the shell — which alone knows where the course was entered
 * from — takes it from there.
 */

const emit = defineEmits<{ (event: 'deleted'): void }>();

const store = useMedicationCheckupStore();

/**
 * Whether the confirmation is up.
 *
 * Held here rather than opened by a trigger of its own: the move starts in
 * the menu, which closes on the click, and a trigger nested in it would take
 * the confirmation down with it.
 */
const confirming = ref(false);

/**
 * Whether the review can be deleted from here.
 */
const deletable = computed(() => store.cycle !== null);

/**
 * Delete the review, then hand the course on.
 *
 * The confirmation stays up while the deletion runs and comes down only once
 * it went through — a refusal is reported by the store, and the pharmacist is
 * still in front of what they asked to delete.
 */
const remove = async () => {
    if (!await store.deleteCheckup()) {
        return;
    }

    confirming.value = false;
    useToastStore().showSuccess({
        content: "Le bilan partagé de médication a été supprimé."
    });
    emit('deleted');
};
</script>

<template>
  <template v-if="deletable">
    <DropdownMenu>
      <DropdownMenuTrigger
        class="shrink-0 p-1 rounded hover:bg-gray-light cursor-pointer"
        aria-label="Actions du bilan"
      >
        <EllipsisVertical class="size-[22px] text-primary" :stroke-width="2.2" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" class="w-64">
        <DropdownMenuItem
          class="cursor-pointer text-red-700 focus:text-red-700"
          @click="confirming = true"
        >
          <Trash2 />
          Supprimer tout le bilan
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <AlertDialog v-model:open="confirming">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Supprimer tout le bilan de ce patient ?</AlertDialogTitle>
          <AlertDialogDescription>
            Le bilan partagé de médication sera supprimé en entier : le cycle
            {{ store.cycle?.rank }} en cours comme les cycles déjà clôturés, avec tout
            ce qui y a été saisi. Cette action est définitive.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter class="items-center">
          <AlertDialogCancel :disabled="store.deleting">
            Annuler
          </AlertDialogCancel>

          <Button
            variant="destructive"
            class="bg-red-600 hover:bg-red-600/90"
            :disabled="store.deleting"
            @click="remove"
          >
            Supprimer tout le bilan
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </template>
</template>
